import { isQueryEnabled } from '@fabric-app/queries/utilities';
import { useIsRevenueCatAvailable } from '@/src/modules/pricing/hooks/useIsRevenueCatAvailable';
import { useSubscriptionAccess } from '@/src/modules/pricing/hooks/useSubscriptionAccess';
import { pricingQueryKeys } from '@/src/modules/pricing/queries/pricingQueryKeys';
import useQueryRevenueCatCustomerInfo from '@/src/modules/pricing/queries/useQueryRevenueCatCustomerInfo';
import type { QueryObserverOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useWoody } from '@fabric-app/business/woody/woody';
import { useQuerySubscription } from '@fabric-app/business/pricing/queries/useQuerySubscription';
import { SubscriptionTier } from '@fabric/woody-client';

export const useQueryPricingManagementUrl = (
  queryOptions?: Partial<QueryObserverOptions<string>>,
) => {
  const { client } = useWoody();
  const { refetch: refetchCustomerInfo } = useQueryRevenueCatCustomerInfo({
    enabled: false,
  });
  const subscriptionAccess = useSubscriptionAccess();
  const isRevenueCatAvailable = useIsRevenueCatAvailable();
  const { data: subscription } = useQuerySubscription();

  return useQuery({
    queryKey: pricingQueryKeys.managementUrl(),
    queryFn: async () => {
      if (!subscriptionAccess.canManageSubscription.allowed)
        throw new Error('Unable to manage subscription from this device');

      if (isRevenueCatAvailable) {
        const customerInfo = await (await refetchCustomerInfo()).data;
        if (!customerInfo) throw new Error('Customer info not found');

        const managementUrl = customerInfo.managementURL;
        if (!managementUrl) throw new Error('Management URL not found');
        return managementUrl;
      }

      if (subscription?.tier === SubscriptionTier.FREE) {
        throw new Error('Free tier does not have a management URL');
      }
      const response = await client.v2('/v2/subscription/portal', {
        method: 'get',
      });

      return response.url;
    },
    ...queryOptions,
    enabled: isQueryEnabled([
      queryOptions?.enabled,
      subscriptionAccess.canManageSubscription.allowed,
    ]),
  });
};
